/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.settings.notification

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.data.repository.notification.NotificationScheduler
import studio.oddpods.cauce.core.data.repository.notification.NotificationSchedulingService
import studio.oddpods.cauce.core.data.repository.notification.NotificationSettingsRepository
import studio.oddpods.cauce.core.model.notification.NotificationPreferences

data class NotificationUiState(
    val isLoading: Boolean = true,
    val isPermissionGranted: Boolean = false,
    val settings: NotificationPreferences = NotificationPreferences.DEFAULT,
)

class NotificationViewModel(
    private val notificationSettingsRepository: NotificationSettingsRepository,
    private val notificationSchedulingService: NotificationSchedulingService,
    private val notificationScheduler: NotificationScheduler,
) : ViewModel() {

    val uiState: StateFlow<NotificationUiState> =
        notificationSettingsRepository.preferences
            .map { prefs ->
                NotificationUiState(
                    isLoading = false,
                    isPermissionGranted = notificationScheduler.hasPermission(),
                    settings = prefs,
                )
            }
            .stateIn(
                scope = viewModelScope,
                started = SharingStarted.WhileSubscribed(5_000),
                initialValue = NotificationUiState(),
            )

    fun onSessionRemindersToggled(enabled: Boolean) {
        viewModelScope.launch {
            notificationSettingsRepository.setSessionRemindersEnabled(enabled)
        }
    }

    fun onPaymentRemindersToggled(enabled: Boolean) {
        viewModelScope.launch {
            notificationSettingsRepository.setPaymentRemindersEnabled(enabled)
            notificationSchedulingService.reschedulePaymentReminders()
        }
    }

    fun onSessionAdvanceMinutesChanged(minutes: Int) {
        viewModelScope.launch {
            notificationSettingsRepository.setSessionAdvanceMinutes(minutes)
        }
    }

    fun onPaymentDayChanged(dayOfWeek: Int) {
        viewModelScope.launch {
            val prefs = notificationSettingsRepository.preferences.value
            notificationSettingsRepository.setPaymentSchedule(
                dayOfWeek = dayOfWeek,
                hour = prefs.paymentReminderHour,
                minute = prefs.paymentReminderMinute,
            )
            notificationSchedulingService.reschedulePaymentReminders()
        }
    }

    fun onPaymentTimeChanged(hour: Int, minute: Int) {
        viewModelScope.launch {
            val prefs = notificationSettingsRepository.preferences.value
            notificationSettingsRepository.setPaymentSchedule(
                dayOfWeek = prefs.paymentReminderDayOfWeek,
                hour = hour,
                minute = minute,
            )
            notificationSchedulingService.reschedulePaymentReminders()
        }
    }

    fun onPermissionGranted() {
        viewModelScope.launch {
            notificationSettingsRepository.setPermissionRequested(true)
            notificationSchedulingService.reschedulePaymentReminders()
        }
    }
}
